import { computed, effect, inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Hex, hashTypedData, parseEventLogs, TransactionReceipt } from 'viem';
import { Address, Difficulty, isCpuAddress, RANDOM_CPU_TEAM_SLOT } from '../data/address-config';
import { environment } from '../../environments/environment';
import { EngineABI } from '../generated/abi/engine';
import { CPUABI } from '../generated/abi/cpu';
import { SignedMatchmakerABI } from '../generated/abi/signed-matchmaker';
import { TEAM_SIZE, ZERO_ADDRESS, ZERO_BYTES_32 } from '../types/constants';

// Inlined copy of the engine's INLINE_STAMINA_REGEN_RULESET, kept here so the eager
// import graph doesn't pull the sim runtime in via the generated Constants module
const INLINE_STAMINA_REGEN_RULESET = '0x000000000000000000000000000000000000057a' as const;
// Inlined copy of the engine's BUILTIN_DUAL_SIGNED_MANAGER sentinel (chomp src/Constants.sol,
// address(0x5165) "SIGS"). Setting battle.moveManager to this opts the battle into the engine's
// built-in dual-signed buffer flow: Engine.startBattle sets usesBuiltinManager = (moveManager ==
// this sentinel). Inlined for the same bundle reason as INLINE_STAMINA_REGEN_RULESET above.
const BUILTIN_DUAL_SIGNED_MANAGER = '0x0000000000000000000000000000000000005165' as const;

// One Multi seat's CPU phantom team config; empty for a human/open seat (the contract skips those).
type PhantomSeat = { monIndices: bigint[]; facetIds: number[]; moveSelections: number[] };

// Canonical open-seat bit for p1 (seat order [p0, p2, p1, p3]) — the mask an open-queue offer
// signs AND submits; the two must be the same value for the signature to verify.
const OPEN_P1_SEAT_MASK = 4;
import {
  BattleOffer,
  deserializeBattleOffer,
  QueueParticipantState,
  RequestType,
  SignedDirectChallengeReceivedPayload,
  SignedQueueMatchedPayload,
  PartyCardPayload,
  PartyReadyPayload,
  PartySeatSpec,
  PartySeatView,
  SeatClaimResultPayload,
  SerializedBattleOffer,
} from '../types/server';
import { MatchmakerStatus, OnchainStateService } from './onchain-state.service';
import { partyRidesCpuHost, resolvePartyInvite } from './party-invite';
import { ServerService } from './server.service';
import { addressEquals } from './utils.service';
import {
  BATTLE_OFFER_EIP712_TYPES,
  signedMatchmakerDomain,
} from './signed-matchmaker-eip712';
import { Wallet } from './wallet';
import { buildCpuOpponentTeam, buildMirrorTeam, CpuTeamBuild, CpuTeamStrategy } from './cpu-team-builder';
import { CPU_TEAMS } from '../generated/cpu-teams';
import { CpuBattlePrefsService } from './cpu-battle-prefs.service';

const SIGNED_MATCHMAKER_DOMAIN = signedMatchmakerDomain(
  environment.chainId,
  Address.SIGNED_MATCHMAKER
);

/** One party seat's assignment. Canonical seats are [p0, p2, p1, p3]; p0 is always self. */
export interface PartySeat {
  kind: 'named' | 'open' | 'cpu';
  address?: Hex; // named seat's human address
}
/** A 4-seat party from the builder: your side = p0 (self) + p2 (ally); opponents = p1 + p3. */
export interface PartyConfig {
  p2: PartySeat; // ally
  p1: PartySeat; // opponent 1 (side-1 lead)
  p3: PartySeat; // opponent 2
}

@Injectable({
  providedIn: 'root',
})
export class ChallengeService {
  private wallet = inject(Wallet);
  private server = inject(ServerService);
  private injector = inject(Injector);
  private router = inject(Router);

  // Battles are serial per client, so one key suffices to dedupe receipt/unicast/broadcast.
  private lastHandledBattleKey: Hex | null = null;

  get teamIndex(): bigint {
    return BigInt(this.onchainState.activeTeamIndex());
  }

  // Client-chosen CPU difficulty for the active battle (the TS-native strategies in services/cpu).
  // Drives which CPU runs and the difficulty reported to belch. (Exp no longer varies by
  // difficulty — every battle earns the flat GAME_EXP_MULT.)
  readonly cpuDifficulty = inject(CpuBattlePrefsService).battleDifficulty;
  // Set when the random opponent team rolled the `mirror` strategy (cpu-team-builder): the CPU plays the
  // mirror MOVE strategy (copies the player's move) instead of the difficulty's CPU. Orthogonal to
  // difficulty (which still drives the CPU strategy / belch tiering).
  readonly cpuMirror = signal<boolean>(false);
  // Selected battle format for the next queue / direct challenge / CPU start: 0 singles, 1 doubles,
  // 2 multi (multi is set by the Custom Game party flow, not the Singles/Doubles toggle). Threaded
  // into the signed BattleOffer + the CPU CustomBattleProposal.
  readonly battleMode = signal<number>(0);
  challengeAccepted: Subject<Hex> = new Subject<Hex>();
  battleReadyToStart: Subject<Hex> = new Subject<Hex>();

  // Server-confirmed queue position. Never set optimistically — every write
  // comes from a server-pushed QUEUE_STATE.
  queueState = signal<QueueParticipantState>({ kind: 'idle' });

  // True while the wallet popup is showing the EIP-712 prompt; disables the
  // join button so multi-clicks can't fire duplicate joins.
  isSigning = signal<boolean>(false);

  readonly isInQueue = computed(() => this.queueState().kind !== 'idle');
  readonly isWaiting = computed(() => this.queueState().kind === 'waiting');
  readonly isMatched = computed(() => {
    const k = this.queueState().kind;
    return k === 'matched_p0' || k === 'matched_p1';
  });
  readonly isChallenging = computed(() => this.queueState().kind === 'challenging');
  matchmakerApprovalPending = signal<boolean>(false);

  private onchainState = inject(OnchainStateService);

  private hasAttemptedApproval = false;
  private lastApprovalAddress: Hex | null = null;

  // The kind of offer this client currently has outstanding. BattleStart bumps the matching
  // on-chain nonce: an open offer (p1 = 0x0) bumps openBattleOfferNonce, a direct challenge bumps
  // the pair-hash nonce. The emitted BattleStart carries the *resolved* opponent as p1, so it can't
  // tell them apart — only what we signed can. Null when we hold no offer (the acceptor side).
  private outstandingOfferKind: 'open' | 'direct' | null = null;

  // Set by postParty (host side) so the PARTY_READY handler can submit the resolved matchup. Holds
  // the blinded Multi offer + the party config; cleared after the submit tx (success or failure).
  private pendingParty: { partyId: string; offer: BattleOffer; cfg: PartyConfig } | null = null;

  // Host's live party lobby (seat fill states from belch); null when not hosting a party.
  readonly partyLobby = signal<{ partyId: string; seats: PartySeatView[] } | null>(null);
  // An incoming party invite for this client (a PARTY_CARD); null when none pending.
  readonly incomingPartyCard = signal<PartyCardPayload | null>(null);
  // Our own seat-claim result (accepted / taken), surfaced to the invite card.
  readonly seatClaimResult = signal<SeatClaimResultPayload | null>(null);

  needsMatchmakerApproval = computed(() => {
    if (!this.wallet.useExternalWalletForTransactions()) return false;
    const status = this.onchainState.matchmakerStatus();
    if (!status || !this.onchainState.hasMons()) return false;
    if (this.matchmakerApprovalPending()) return false;
    return !status.isCPUApproved || !status.isSignedMatchmakerApproved;
  });

  // Shared by every CPU and boss entry point; startBattle reverts without a live team.
  readonly canStartCpuBattle = computed(
    () =>
      this.onchainState.hasTeam() === true &&
      !this.isInQueue() &&
      !this.isSigning() &&
      !this.matchmakerApprovalPending() &&
      !this.needsMatchmakerApproval(),
  );

  constructor() {
    this.server.battleStart.subscribe(async (battleData) => {
      const me = this.wallet.activeAddress();
      if (!me) return;
      // Party seat-claimers land in p2/p3, not just p0/p1 — match all four so an ally / second
      // opponent routes into the battle too.
      const isParticipant =
        addressEquals(battleData.p0, me) ||
        addressEquals(battleData.p1, me) ||
        (!!battleData.p2 && addressEquals(battleData.p2, me)) ||
        (!!battleData.p3 && addressEquals(battleData.p3, me));
      if (!isParticipant) return;
      // The HOST of a CPU battle navigates via challengeAccepted; skip its broadcast to avoid a
      // double-emit. A human ally (p2) of a CPU-host battle has no such path, so it must NOT skip.
      if (isCpuAddress(battleData.p1) && addressEquals(battleData.p0, me)) return;
      this.handleBattleStarted(battleData.battleKey, battleData.p0);
    });

    this.challengeAccepted.subscribe((battleKey) => {
      this.battleReadyToStart.next(battleKey);
    });

    // Root-owned so a queued player who leaves home still gets matched and routed in.
    this.battleReadyToStart.subscribe((battleKey) => {
      this.router.navigate(['/battle', battleKey]);
    });

    this.server.signedQueueMatched.subscribe((payload) => {
      this.acceptSignedQueueMatch(payload).catch((error) => {
        console.error('Error auto-accepting signed queue match:', error);
      });
    });

    this.server.queueState.subscribe(({ state }) => {
      this.queueState.set(state);
      // Server dropped us back to idle (left queue / rejected / expired) — the offer is gone.
      if (state.kind === 'idle') this.outstandingOfferKind = null;
    });

    this.server.reconnected.subscribe(() => {
      this.server.queueMessage({ type: RequestType.QUERY_QUEUE_STATE });
    });

    // Host side: belch resolved every seat — submit the matchup on-chain (CPU-inclusive → CPU
    // phantom multi; all-human → SignedMatchmaker.startGame). Guard on our own pending posting.
    this.server.partyReady$.subscribe((ready) => {
      if (ready.partyId !== this.pendingParty?.partyId) return;
      void this.submitPartyReady(ready);
    });

    // Host: live seat fill states. An empty seats array is belch's cancelled/expired sentinel.
    this.server.partyUpdated$.subscribe((upd) => {
      if (upd.partyId !== this.pendingParty?.partyId) return;
      if (upd.seats.length === 0) {
        this.clearParty();
        return;
      }
      this.partyLobby.set({ partyId: upd.partyId, seats: upd.seats });
    });
    // Invitee: a party offered us a seat.
    this.server.partyCard$.subscribe((card) => {
      const invite = resolvePartyInvite(card, {
        self: this.wallet.activeAddress(),
        hosting: this.partyLobby() !== null,
        held: this.incomingPartyCard(),
        claimAccepted: this.seatClaimResult()?.accepted === true,
      });
      if (!invite) return;
      this.incomingPartyCard.set(invite);
      this.seatClaimResult.set(null);
    });
    this.server.seatClaimResult$.subscribe((res) => this.seatClaimResult.set(res));

    // External wallets approve via the UI prompt (`needsMatchmakerApproval` +
    // `triggerMatchmakerApproval`); hot wallets auto-approve here.
    effect(() => {
      const status = this.onchainState.matchmakerStatus();
      const hasMons = this.onchainState.hasMons();
      const isLoaded = this.onchainState.isLoaded();
      const activeAddress = this.onchainState.activeAddress();

      // Approval is a standing grant, not a per-battle step: gate it on the account
      // existing, not on a team, or an account stuck without one can never self-heal.
      if (!isLoaded || !status || !hasMons || !activeAddress) {
        return;
      }

      if (this.lastApprovalAddress !== activeAddress) {
        this.hasAttemptedApproval = false;
        this.lastApprovalAddress = activeAddress;
      }

      if (this.hasAttemptedApproval) {
        return;
      }
      this.hasAttemptedApproval = true;

      if (this.wallet.useExternalWalletForTransactions()) {
        return;
      }

      this.approveMatchmakersIfNeeded(status).catch(() => {});
    });
  }

  async triggerMatchmakerApproval(): Promise<void> {
    const status = this.onchainState.matchmakerStatus();
    if (!status) return;
    await this.approveMatchmakersIfNeeded(status);
  }

  private async approveMatchmakersIfNeeded(status: MatchmakerStatus) {
    // Collapsed CPU: only the single CPU host + SignedMatchmaker (PvP) need approving.
    const addressesToApprove: Hex[] = [];
    if (!status.isCPUApproved) {
      addressesToApprove.push(Address.CPU);
    }
    if (!status.isSignedMatchmakerApproved) {
      addressesToApprove.push(Address.SIGNED_MATCHMAKER);
    }
    if (addressesToApprove.length > 0) {
      // Set pending state if using external wallet (requires user confirmation)
      if (this.wallet.useExternalWalletForTransactions()) {
        this.matchmakerApprovalPending.set(true);
      }
      try {
        await this.wallet.sendRealtimeRawTransaction({
          address: Address.ENGINE,
          abi: EngineABI,
          functionName: 'updateMatchmakers',
          args: [addressesToApprove, []],
          ...this.wallet.burnerTx,
          gas: 200000n
        });
        if (!status.isCPUApproved) {
          this.onchainState.setMatchmakerApproved('cpu', true);
        }
        if (!status.isSignedMatchmakerApproved) {
          this.onchainState.setMatchmakerApproved('signed', true);
        }
      } finally {
        this.matchmakerApprovalPending.set(false);
      }
    }
  }

  async createRandomCPUChallenge(difficulty: Difficulty) {
    const build = buildCpuOpponentTeam(this.onchainState.monIndicesForTeam());
    await this.startCustomCpuBattle(build, difficulty);
  }

  // Force a mirror battle (vs. the ~1/6 random roll); needs a full team for move-mirror symmetry.
  async createMirrorCPUChallenge(difficulty: Difficulty): Promise<boolean> {
    const playerMonIds = this.onchainState.monIndicesForTeam();
    if (playerMonIds.length < TEAM_SIZE) return false;
    await this.startCustomCpuBattle(buildMirrorTeam(playerMonIds), difficulty);
    return true;
  }

  async startCustomCpuBattle(
    build: Pick<CpuTeamBuild, 'monIndices' | 'facetIds'> & {
      strategy?: CpuTeamStrategy;
      moveSelections?: readonly number[];
    },
    difficulty: Difficulty,
    cpuTeamSlot: number = RANDOM_CPU_TEAM_SLOT,
    // Daily-challenge overrides: the encounter fixes the field ruleset and the format, so
    // neither may fall back to the player's own format preference. dailyDay marks the run as
    // a boss attempt for the lobby feed.
    opts: { ruleset?: Hex; battleMode?: number; dailyDay?: number } = {},
  ) {
    const activeAddr = this.requireActiveAddress();
    const cpuAddress = Address.CPU;
    const { monIndices, facetIds, strategy } = build;
    // A mirror team => the CPU also mirrors the player's MOVES (see local-cpu-game's dispatch).
    this.cpuMirror.set(strategy === 'mirror');
    const receipt = await this.wallet.sendRealtimeRawTransaction({
      address: cpuAddress,
      abi: CPUABI,
      functionName: 'startCustomBattle',
      args: [
        {
          p0: activeAddr,
          p0TeamIndex: this.teamIndex,
          monIndices,
          facetIds,
          moveSelections: build.moveSelections ?? monIndices.map(() => 0),
          ...this.cpuBattleTail(cpuAddress, opts.ruleset),
          battleMode: opts.battleMode ?? this.battleMode(),
        },
      ],
      ...this.wallet.burnerTx,
    });
    const battleStart = this.findBattleStart(receipt);
    if (!battleStart) return;
    this.reportCpuBattle(battleStart.args.battleKey, difficulty, cpuTeamSlot, opts.dailyDay);
    this.challengeAccepted.next(battleStart.args.battleKey);
  }

  // Find the battle-start log in a receipt — singles emit BattleStart, 2-slot modes emit
  // SlotBattleStart (same leading battleKey/p0/p1 args). Engine hooks (e.g. GachaTeamRegistry)
  // can emit their own logs before the Engine's, so match by event name rather than logs[0].
  private findBattleStart(receipt: TransactionReceipt) {
    return parseEventLogs({
      abi: EngineABI,
      logs: receipt.logs,
      eventName: ['BattleStart', 'SlotBattleStart'],
    })[0];
  }

  // Record the chosen difficulty locally and tell belch (trust-the-client) so stats tier correctly,
  // since the collapsed CPU's on-chain address no longer encodes easy/medium/hard. cpuTeamSlot is
  // the numbered team index (0..2) or RANDOM_CPU_TEAM_SLOT — belch keys per-team CPU stats off it.
  private reportCpuBattle(
    battleKey: Hex,
    difficulty: Difficulty,
    cpuTeamSlot: number,
    dailyDay?: number,
  ): void {
    this.cpuDifficulty.set(difficulty);
    this.server.queueMessage({
      type: RequestType.REPORT_CPU_BATTLE,
      payload: { battleKey, difficulty, cpuTeamSlot, dailyDay },
    });
  }

  async createCPUChallenge(cpuTeamIndex: bigint, difficulty: Difficulty) {
    // The CPU contract's startBattle is singles-only (no mode field). Doubles replays the
    // numbered team as a phantom config via startCustomBattle, which carries battleMode.
    if (this.battleMode() !== 0) {
      const monIndices = CPU_TEAMS[Number(cpuTeamIndex)].map((id) => BigInt(id));
      // Numbered registry teams are facet-less; zeros keep the replica identical.
      await this.startCustomCpuBattle(
        { monIndices, facetIds: monIndices.map(() => 0) },
        difficulty,
        Number(cpuTeamIndex),
      );
      return;
    }
    const activeAddr = this.requireActiveAddress();
    // Mirror is a random-challenge event only — a stale flag would hijack this battle's CPU
    // profile (and the mirror battle backdrop).
    this.cpuMirror.set(false);
    const cpuAddress = Address.CPU;
    const receipt = await this.wallet.sendRealtimeRawTransaction({
      address: cpuAddress,
      abi: CPUABI,
      functionName: 'startBattle',
      args: [
        {
          p0: activeAddr,
          p0TeamIndex: this.teamIndex,
          p0TeamHash: ZERO_BYTES_32,
          p1: cpuAddress,
          p1TeamIndex: cpuTeamIndex,
          ...this.cpuBattleTail(cpuAddress),
        },
      ],
      ...this.wallet.burnerTx,
      gas: 3000000n
    });
    const battleStart = this.findBattleStart(receipt);
    if (!battleStart) return;
    this.reportCpuBattle(battleStart.args.battleKey, difficulty, Number(cpuTeamIndex));
    this.challengeAccepted.next(battleStart.args.battleKey);
  }

  private requireActiveAddress(): Lowercase<Hex> {
    const activeAddr = this.wallet.activeAddress();
    if (!activeAddr) {
      throw new Error('Wallet not connected');
    }
    return activeAddr;
  }

  // The CPU host is its own move manager + matchmaker, so every CPU battle struct shares this tail.
  // `ruleset` defaults to plain inline regen; the daily challenge passes its field ruleset instead.
  private cpuBattleTail(cpu: Hex, ruleset: Hex = INLINE_STAMINA_REGEN_RULESET as Hex) {
    return {
      teamRegistry: Address.GACHA_TEAM_REGISTRY,
      rngOracle: ZERO_ADDRESS as Hex,
      ruleset,
      moveManager: cpu,
      matchmaker: cpu,
      engineHooks: [Address.GACHA_TEAM_REGISTRY],
    };
  }

  // The shared tail of a signed BattleOffer's battle struct (everything past the seat addresses):
  // standard registry/ruleset + the built-in dual-signed move manager + the signed matchmaker.
  private standardBattleTail() {
    return {
      teamRegistry: Address.GACHA_TEAM_REGISTRY,
      rngOracle: ZERO_ADDRESS as Hex,
      ruleset: INLINE_STAMINA_REGEN_RULESET as Hex,
      moveManager: BUILTIN_DUAL_SIGNED_MANAGER as Hex,
      matchmaker: Address.SIGNED_MATCHMAKER,
      engineHooks: [Address.GACHA_TEAM_REGISTRY],
    };
  }

  private createBattleOffer(p1: Hex, nonce: bigint, battleMode: number = this.battleMode()): BattleOffer {
    const activeAddr = this.requireActiveAddress();
    // Signing form is fully blinded (D31): all team indices zeroed, open seats zeroed. p0's real
    // index is carried as the p0TeamIndex sibling on the wire and filled into calldata by the
    // submitter. Singles: p2/p3 vacant, battleMode 0.
    return {
      battle: {
        p0: activeAddr,
        p0TeamIndex: 0n,
        p1: p1,
        p1TeamIndex: 0n,
        p2: ZERO_ADDRESS,
        p2TeamIndex: 0n,
        p3: ZERO_ADDRESS,
        p3TeamIndex: 0n,
        ...this.standardBattleTail(),
      },
      pairHashNonce: nonce,
      battleMode,
    };
  }

  /**
   * Sign a BattleOffer using EIP-712 typed data.
   * openSeatsMask is a signed field: it must equal the mask later submitted to startGame.
   */
  private async signBattleOffer(offer: BattleOffer, openSeatsMask: number): Promise<Hex> {
    return this.wallet.signTypedData({
      domain: SIGNED_MATCHMAKER_DOMAIN,
      types: BATTLE_OFFER_EIP712_TYPES,
      primaryType: 'BattleOffer',
      message: { ...offer, openSeatsMask } as unknown as Record<string, unknown>,
    });
  }

  // ── Multi (2v2) party matchmaking ──────────────────────────────────────────
  // Any CPU seat routes the party to CPU.startCustomMultiBattle (phantom configs, host-relayed);
  // an all-human party uses SignedMatchmaker.startGame + per-open-seat SeatFill. The builder's
  // ≥1-human-per-side rule guarantees at least one human opponent, so a party is always lobbied.

  hasCpuSeat(cfg: PartyConfig): boolean {
    return cfg.p2.kind === 'cpu' || cfg.p1.kind === 'cpu' || cfg.p3.kind === 'cpu';
  }

  // The built-in rotation needs a human per side (else no revealer). Side 0 always has the host, so
  // only side 1 (p1 + p3) can be fully CPU — that battle must ride the CPU-host path instead. A CPU
  // ally alongside a human (each side still human) stays on the rotation path, authored locally.
  sideFullyCpu(cfg: PartyConfig): boolean {
    return cfg.p1.kind === 'cpu' && cfg.p3.kind === 'cpu';
  }

  // Canonical mask bits: p2 = 2, p1 = 4, p3 = 8 (p0 = bit 0 is never open).
  computeOpenSeatsMask(cfg: PartyConfig): number {
    return (
      (cfg.p2.kind === 'open' ? 2 : 0) |
      (cfg.p1.kind === 'open' ? 4 : 0) |
      (cfg.p3.kind === 'open' ? 8 : 0)
    );
  }

  // The blinded MULTI BattleOffer (all-human path): open-seat addresses zeroed, every team index
  // zeroed (the contract re-derives the same digest from calldata carrying real values). Named
  // seats carry their real address; CPU seats carry the CPU host address.
  createMultiOffer(cfg: PartyConfig, nonce: bigint): BattleOffer {
    const activeAddr = this.requireActiveAddress();
    const addrFor = (s: PartySeat): Hex =>
      s.kind === 'named' ? (s.address ?? ZERO_ADDRESS) : s.kind === 'cpu' ? (Address.CPU as Hex) : ZERO_ADDRESS;
    return {
      battle: {
        p0: activeAddr,
        p0TeamIndex: 0n,
        p1: addrFor(cfg.p1),
        p1TeamIndex: 0n,
        p2: addrFor(cfg.p2),
        p2TeamIndex: 0n,
        p3: addrFor(cfg.p3),
        p3TeamIndex: 0n,
        ...this.standardBattleTail(),
      },
      pairHashNonce: nonce,
      battleMode: 2, // BATTLE_MODE_MULTI
    };
  }

  // The blinded offer's EIP-712 digest — what a named seat signs (plain) and an open-seat claimer
  // wraps in a SeatFill. Must match the contract's _hashTypedData(hashBattleOfferForSigning).
  offerDigest(offer: BattleOffer, openSeatsMask: number): Hex {
    return hashTypedData({
      domain: SIGNED_MATCHMAKER_DOMAIN,
      types: BATTLE_OFFER_EIP712_TYPES,
      primaryType: 'BattleOffer',
      message: { ...offer, openSeatsMask } as unknown as Record<string, unknown>,
    });
  }

  // A joiner signs a SeatFill over the offer digest + their canonical seat index — binding consent
  // to that specific seat so it can't be replayed elsewhere.
  async signSeatFill(offer: BattleOffer, openSeatsMask: number, seatIndex: number): Promise<Hex> {
    return this.wallet.signTypedData({
      domain: SIGNED_MATCHMAKER_DOMAIN,
      types: { SeatFill: [{ name: 'offerDigest', type: 'bytes32' }, { name: 'seatIndex', type: 'uint8' }] },
      primaryType: 'SeatFill',
      message: { offerDigest: this.offerDigest(offer, openSeatsMask), seatIndex },
    });
  }

  // Host posts the party to belch's lobby. Both paths post the blinded Multi offer — it's the
  // lobby's matchup representation; only the all-human path ever submits it on-chain (at PARTY_READY).
  cancelParty(): void {
    const p = this.pendingParty;
    if (p) this.server.queueMessage({ type: RequestType.CANCEL_PARTY, payload: { partyId: p.partyId } });
    this.clearParty();
  }
  private clearParty(): void {
    this.pendingParty = null;
    this.partyLobby.set(null);
  }
  dismissInvite(): void {
    this.incomingPartyCard.set(null);
    this.seatClaimResult.set(null);
  }

  async postParty(cfg: PartyConfig): Promise<void> {
    const self = this.requireActiveAddress();

    const nonce = await this.onchainState.refreshOpenBattleOfferNonce();
    const offer = this.createMultiOffer(cfg, nonce);
    const hostSignature = await this.signBattleOffer(offer, this.computeOpenSeatsMask(cfg));

    const seatFor = (s: PartySeat): PartySeatSpec => ({
      kind: s.kind,
      address: s.kind === 'named' ? s.address : undefined,
    });
    // Canonical seat order [p0, p2, p1, p3]; p0 is always the host (self).
    const seats: PartySeatSpec[] = [
      { kind: 'host', address: self },
      seatFor(cfg.p2),
      seatFor(cfg.p1),
      seatFor(cfg.p3),
    ];

    const partyId = crypto.randomUUID();
    this.pendingParty = { partyId, offer, cfg };

    this.server.queueMessage({
      type: RequestType.POST_PARTY,
      payload: {
        partyId,
        // Send the native offer (bigints) and let bigintReplacer stringify — same serialization the
        // signed queue uses; the cast just satisfies the serialized-on-the-wire payload type.
        offer: offer as unknown as SerializedBattleOffer,
        openSeatsMask: this.computeOpenSeatsMask(cfg),
        seats,
        isCpuInclusive: this.hasCpuSeat(cfg),
        hostSignature,
        p0TeamIndex: this.teamIndex.toString(),
      },
    });

    // Show the lobby immediately (host + cpu seats pre-filled); belch's PARTY_UPDATED refines it.
    this.partyLobby.set({
      partyId,
      seats: seats.map((s) => ({
        kind: s.kind,
        address: s.address,
        filled: s.kind === 'host' || s.kind === 'cpu',
      })),
    });
  }

  // A named invitee / open-seat taker consents to the seat belch offered them.
  async claimSeat(card: PartyCardPayload): Promise<void> {
    const seatIndex = card.yourSeatIndex;
    if (seatIndex == null) throw new Error('Party card carries no seat for this client');
    const offer = deserializeBattleOffer(card.offer);

    // Only a fully-CPU side relay-joins (the host runs the phantom multi on-chain). Everything else
    // needs consent: an open seat signs a SeatFill bound to its index; a named seat, the offer digest.
    let seatSig: Hex | undefined;
    if (!partyRidesCpuHost(card.seats)) {
      seatSig =
        card.seats[seatIndex].kind === 'open'
          ? await this.signSeatFill(offer, card.openSeatsMask, seatIndex)
          : await this.signBattleOffer(offer, card.openSeatsMask);
    }

    this.server.queueMessage({
      type: RequestType.CLAIM_SEAT,
      payload: {
        partyId: card.partyId,
        seatIndex,
        seatSig,
        teamIndex: this.teamIndex.toString(),
      },
    });
  }

  // Host-only: belch resolved every seat. Submit the concrete matchup on-chain.
  private async submitPartyReady(ready: PartyReadyPayload): Promise<void> {
    const { offer, cfg } = this.pendingParty!;
    try {
      if (this.sideFullyCpu(cfg)) {
        await this.submitCpuMultiBattle(ready, cfg);
      } else {
        await this.submitMultiStartGame(ready, offer, cfg);
      }
    } catch (error) {
      console.error('[PartyBuilder] Multi battle submit failed', error);
    } finally {
      this.pendingParty = null;
    }
  }

  // Map the resolved party's canonical order [p0, p2, p1, p3] (addresses/teamIndices [0..3]) onto the
  // engine struct's p0/p1/p2/p3 seats. p0 is always us, so it carries our own real team index; a null
  // resolved address falls back per caller (a CPU seat → the CPU address, an all-human → the blinded offer).
  private resolveStructSeats(ready: PartyReadyPayload, fallback: { p0: Hex; p1: Hex; p2: Hex; p3: Hex }) {
    return {
      p0: ready.addresses[0] ?? fallback.p0,
      p0TeamIndex: this.teamIndex,
      p1: ready.addresses[2] ?? fallback.p1,
      p1TeamIndex: BigInt(ready.teamIndices[2]),
      p2: ready.addresses[1] ?? fallback.p2,
      p2TeamIndex: BigInt(ready.teamIndices[1]),
      p3: ready.addresses[3] ?? fallback.p3,
      p3TeamIndex: BigInt(ready.teamIndices[3]),
    };
  }

  // CPU phantom team config per struct seat (p1, p2, p3); a CPU seat gets a built team, others empty.
  private buildSeatConfigs(cfg: PartyConfig): [PhantomSeat, PhantomSeat, PhantomSeat] {
    const seatPhantom = (s: PartySeat): PhantomSeat => {
      if (s.kind !== 'cpu') return { monIndices: [], facetIds: [], moveSelections: [] };
      const build = buildCpuOpponentTeam(this.onchainState.monIndicesForTeam());
      return { monIndices: build.monIndices, facetIds: build.facetIds, moveSelections: build.monIndices.map(() => 0) };
    };
    return [seatPhantom(cfg.p1), seatPhantom(cfg.p2), seatPhantom(cfg.p3)];
  }

  // CPU-inclusive party → CPU.startCustomMultiBattle. seatConfigs align to struct p1/p2/p3.
  private async submitCpuMultiBattle(ready: PartyReadyPayload, cfg: PartyConfig): Promise<void> {
    const cpu = Address.CPU as Hex;
    const seatConfigs = this.buildSeatConfigs(cfg);
    const receipt = await this.wallet.sendRealtimeRawTransaction({
      address: cpu,
      abi: CPUABI,
      functionName: 'startCustomMultiBattle',
      args: [
        {
          ...this.resolveStructSeats(ready, { p0: this.wallet.activeAddress() as Hex, p1: cpu, p2: cpu, p3: cpu }),
          seatConfigs,
          ...this.cpuBattleTail(cpu),
        },
      ],
      ...this.wallet.burnerTx,
      gas: 3000000n,
    });
    const battleStart = this.findBattleStart(receipt);
    if (battleStart) this.challengeAccepted.next(battleStart.args.battleKey);
  }

  // Human/CPU-ally party → SignedMatchmaker.startGameWithSeatConfigs: fill the blinded offer's
  // calldata with the resolved seats, then submit with the open-seat mask + sigs + CPU phantom
  // configs (empty for an all-human party — the contract writes phantoms only for whitelisted seats).
  private async submitMultiStartGame(ready: PartyReadyPayload, offer: BattleOffer, cfg: PartyConfig): Promise<void> {
    // Canonical→struct fill; p0 is us (msg.sender), so it keeps our own real team index, not the
    // possibly-'0' relayed one. A null resolved address falls back to the blinded offer's value.
    Object.assign(
      offer.battle,
      this.resolveStructSeats(ready, {
        p0: offer.battle.p0,
        p1: offer.battle.p1,
        p2: offer.battle.p2,
        p3: offer.battle.p3,
      }),
    );

    const openSeatsMask = this.computeOpenSeatsMask(cfg);
    // Canonical seatSigs [p0, p2, p1, p3]; seat 0 = host = msg.sender, satisfied by being the sender.
    const seatSigs = ready.seatSigs.map((s) => s ?? '0x') as [Hex, Hex, Hex, Hex];
    seatSigs[0] = '0x';

    const receipt = await this.wallet.sendRealtimeRawTransaction({
      address: Address.SIGNED_MATCHMAKER,
      abi: SignedMatchmakerABI,
      functionName: 'startGameWithSeatConfigs',
      args: [offer, openSeatsMask, seatSigs, this.buildSeatConfigs(cfg)],
      ...this.wallet.burnerTx,
      gas: 3000000n,
    });
    const battleStart = this.findBattleStart(receipt);
    if (battleStart) this.handleBattleStarted(battleStart.args.battleKey, battleStart.args.p0);
  }

  /**
   * Join the signed queue with a pre-signed open battle offer.
   * P0 signs an offer with p1 = 0x0 and waits for a match.
   */
  // opts come from tapping a QUEUE_LISTINGS row: queue into that listing's lane and
  // ask belch to prefer that waiter. The toggle follows the lane so the format the
  // queue-format-drill locks and displays matches the one we're actually queued in.
  async joinSignedQueue(opts?: { battleMode?: number; preferOpponent?: Hex }): Promise<void> {
    this.requireActiveAddress();
    if (this.isSigning() || this.isInQueue()) return;

    if (opts?.battleMode !== undefined) this.battleMode.set(opts.battleMode);

    this.isSigning.set(true);
    try {
      const nonce = await this.onchainState.refreshOpenBattleOfferNonce();
      const offer = this.createBattleOffer(ZERO_ADDRESS, nonce);
      // Open-queue offer: p1 is the lone open seat, and the acceptor submits that same mask.
      const signature = await this.signBattleOffer(offer, OPEN_P1_SEAT_MASK);

      this.outstandingOfferKind = 'open';
      this.server.queueMessage({
        type: RequestType.JOIN_SIGNED_QUEUE,
        payload: {
          offer,
          signature,
          p0TeamIndex: this.teamIndex.toString(),
          preferOpponent: opts?.preferOpponent,
        },
      });
    } finally {
      this.isSigning.set(false);
    }
  }

  /**
   * Leave the signed queue. Only meaningful in `waiting`; matched_* states
   * are not leavable (server will respond with the unchanged state).
   */
  leaveSignedQueue(): void {
    this.server.queueMessage({
      type: RequestType.LEAVE_SIGNED_QUEUE,
    });
  }


  async acceptSignedQueueMatch(payload: SignedQueueMatchedPayload): Promise<void> {
    try {
      await this.submitStartGame(payload);
    } catch (error) {
      this.outstandingOfferKind = null;
      this.queueState.set({ kind: 'idle' });
      throw error;
    }
  }

  async createSignedDirectChallenge(
    opponentAddress: Hex,
    battleMode: number = this.battleMode(),
  ): Promise<void> {
    this.requireActiveAddress();

    const nonce = await this.onchainState.getPairHashNonce(opponentAddress);
    const offer = this.createBattleOffer(opponentAddress, nonce, battleMode);
    // Direct challenge: every seat is named, so the closed offer signs mask 0.
    const signature = await this.signBattleOffer(offer, 0);

    this.outstandingOfferKind = 'direct';
    this.server.queueMessage({
      type: RequestType.SIGNED_DIRECT_CHALLENGE,
      payload: { offer, signature, p1: opponentAddress, p0TeamIndex: this.teamIndex.toString() },
    });
  }

  async acceptSignedDirectChallenge(payload: SignedDirectChallengeReceivedPayload): Promise<void> {
    await this.submitStartGame(payload);
  }

  private async submitStartGame(
    payload: SignedQueueMatchedPayload | SignedDirectChallengeReceivedPayload
  ): Promise<void> {
    const activeAddr = this.requireActiveAddress();

    const offer = deserializeBattleOffer(payload.offer);
    // The signed offer is blinded (indices 0). Fill real team indices into calldata: p0's from
    // the relayed sibling, p1's (submitter = us) from our own selection. Singles → p2/p3 vacant.
    offer.battle.p0TeamIndex = BigInt(payload.p0TeamIndex);
    offer.battle.p1TeamIndex = this.teamIndex;
    // Open-queue offers blind p1 to 0x0; fill our address into the seat and mark it open so p0's
    // signature verifies against the blinded digest (being msg.sender then satisfies the seat).
    let openSeatsMask = 0;
    if (addressEquals(offer.battle.p1, ZERO_ADDRESS)) {
      offer.battle.p1 = activeAddr;
      openSeatsMask = OPEN_P1_SEAT_MASK;
    }
    // startGame(offer, openSeatsMask, bytes[4] seatSigs). Canonical seat order [p0, p2, p1, p3]:
    // seat 0 = p0 is satisfied by its offer signature; seat 2 = p1 (us) by being msg.sender;
    // p2/p3 vacant.
    const seatSigs: [Hex, Hex, Hex, Hex] = [payload.signature, '0x', '0x', '0x'];
    const receipt = await this.wallet.sendRealtimeRawTransaction({
      address: Address.SIGNED_MATCHMAKER,
      abi: SignedMatchmakerABI,
      functionName: 'startGame',
      args: [offer, openSeatsMask, seatSigs],
      ...this.wallet.burnerTx,
      gas: 3000000n,
    });

    // The realtime receipt already carries BattleStart — navigate off it now instead of waiting
    // for the chain→belch→WS rebroadcast; that rebroadcast dedupes in handleBattleStarted. Scan
    // logs rather than indexing: engine hooks (GachaTeamRegistry) can emit before the Engine.
    const battleStart = this.findBattleStart(receipt);
    if (battleStart) {
      this.handleBattleStarted(battleStart.args.battleKey, battleStart.args.p0);
    }
  }

  // Shared entry for "this PvP battle started": fired from the submitter's startGame receipt and
  // from the belch rebroadcast (P0 can even get a unicast AND a broadcast) — first one wins, the
  // rest dedupe, so the offer nonce can't double-bump.
  private handleBattleStarted(battleKey: Hex, p0: Hex): void {
    if (this.lastHandledBattleKey?.toLowerCase() === battleKey.toLowerCase()) return;
    this.lastHandledBattleKey = battleKey;

    // Only an OPEN offer bumps openBattleOfferNonce on chain (a direct challenge bumps the
    // pair-hash nonce instead). p0 is the offer signer; gate on what we actually signed, not the
    // event's resolved p1 — that's the opponent in both the open and direct cases.
    if (this.outstandingOfferKind === 'open' && addressEquals(p0, this.wallet.activeAddress())) {
      this.onchainState.incrementOpenBattleOfferNonce();
    }
    this.outstandingOfferKind = null;
    // BattleStart is the terminal signal — server drops the participant
    // entry without sending a follow-up QUEUE_STATE, so clear locally.
    this.queueState.set({ kind: 'idle' });

    // Warm the battle snapshot while the route mounts (dynamic import — BattleLoader statically
    // injects this service, so a static edge back would be a cycle).
    void import('./battle-loader.service').then(({ BattleLoader }) => {
      this.injector.get(BattleLoader).prefetchBattle(battleKey);
    });

    this.battleReadyToStart.next(battleKey);
  }
}
